import { CompanyPreferences } from "@type/company";
import { cn } from "@util/tailwind";

export interface ProgressStep {
    label: string;
    isActive: boolean;
    isCompleted: boolean;
    isHide?: boolean;
    className?: string;
}

interface ProgressBarProps {
    currentStep: number;
    steps: ProgressStep[];
    companyPreferences?: CompanyPreferences | undefined;
    isShowBanding?: boolean;
}

export function ProgressBar({ currentStep, steps, companyPreferences = undefined, isShowBanding = false }: ProgressBarProps) {
    const progressPercentage = ((currentStep - 1) / (steps.length - 1)) * 100;

    return (
        <div className="w-full">
            <div className="hidden md:flex flex-row gap-6 w-full">
                {steps.map((step, index) => !step.isHide && (
                    <div key={index} className="w-full">
                        <div
                            className={`relative h-2 rounded-full ${step.isCompleted
                                ? `${!isShowBanding && "bg-blue-700"}`
                                : step.isActive
                                    ? step.className || `${!isShowBanding && "bg-blue-700"}`
                                    : "bg-gray-200 dark:bg-gray-800"
                                } overflow-hidden`}
                            style={isShowBanding && companyPreferences && (step.isCompleted || step.isActive) ? {
                                backgroundColor: companyPreferences?.primary_color || '#1d4ed8'
                            } : undefined}
                        >
                            {index === 0 && step.isActive && !step.isCompleted && (
                                <div
                                    className={`absolute top-0 left-0 h-full ${step.className || `${!isShowBanding && "bg-blue-700"}`}`}
                                    style={{ width: "50%" }}
                                />
                            )}

                            {index === 1 && step.isActive && !step.isCompleted && (
                                <div
                                    className={`absolute top-0 left-0 h-full ${step.className || `${!isShowBanding && "bg-blue-700"}`}`}
                                    style={{ width: "50%" }}
                                />
                            )}
                        </div>
                        <div className={cn("mt-4 text-center sm:block", {
                            "hidden": steps.length > 3
                        })}>
                            <span
                                className={`text-center font-medium ${step.isActive || step.isCompleted
                                    ? step.className ? "text-black" : `${!isShowBanding && "text-blue-600"} dark:text-blue-400`
                                    : "text-gray-500 dark:text-gray-400"
                                    }`}
                                style={isShowBanding && companyPreferences && (step.isCompleted || step.isActive) ? {
                                    color: companyPreferences?.primary_color
                                } : undefined}
                            >
                                {step.label}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="md:hidden flex flex-col gap-2 w-full">
                {currentStep === 0 ? (
                    <div className="w-full">
                        <div className="flex items-start w-full">
                            {steps.filter((step) => !step.isHide).map((step, index, arr) => (
                                <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                                    <div className="flex items-center w-full">
                                        <div className={`flex-1 h-px ${index === 0 ? "invisible" : "bg-gray-200 dark:bg-gray-700"}`} />
                                        <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <span className="text-[9px] font-semibold text-gray-500 dark:text-gray-400">{index + 1}</span>
                                        </div>
                                        <div className={`flex-1 h-px ${index === arr.length - 1 ? "invisible" : "bg-gray-200 dark:bg-gray-700"}`} />
                                    </div>
                                    <span className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 font-medium text-center px-0.5 leading-tight">{step.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Progress line container */}
                        <div
                            className="relative w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full"
                        >
                            {/* Active progress line */}
                            <div
                                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-out ${!isShowBanding && "bg-blue-600 "}`}
                                style={{
                                    width: `${Math.max(progressPercentage, 8)}%`,
                                    backgroundColor: isShowBanding && companyPreferences ? companyPreferences?.primary_color : undefined,
                                }}
                            />
                        </div>

                        {/* Text information below the progress line */}
                        <div className="flex justify-between items-center text-sm">
                            <span
                                className="text-gray-900 dark:text-gray-100 font-medium"
                                style={{ color: isShowBanding && companyPreferences ? companyPreferences?.primary_color : "#2563eb" }}
                            >
                                {steps.find((step) => step.isActive)?.label || "Progress"}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400">
                                Step {currentStep > steps.filter((step) => !step.isHide).length ? steps.filter((step) => !step.isHide).length : currentStep} of {steps.filter((step) => !step.isHide).length}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}